import { isAxiosError } from 'axios';

/**
 * A cookie-mode logout whose `/auth/logout` has not been confirmed (PRX-1745).
 *
 * The session cookie is HttpOnly, so only Go can end it. If Go was down at
 * logout, or the tab closed first, the cookie would live on and the next page
 * load would restore the last user's session. This marker survives that: the
 * next cookie-mode boot ends the session before it restores anything.
 *
 * It holds a timestamp, never a secret.
 */
export const LOGOUT_PENDING_KEY = 'logout-pending';

/** Records that a logout still has to reach Go. */
export function markLogoutPending(): void {
  localStorage.setItem(LOGOUT_PENDING_KEY, String(Date.now()));
}

/** Go has ended the session, or it was already gone. */
export function clearLogoutPending(): void {
  localStorage.removeItem(LOGOUT_PENDING_KEY);
}

/** True while a logout still has to reach Go. */
export function isLogoutPending(): boolean {
  return localStorage.getItem(LOGOUT_PENDING_KEY) !== null;
}

/**
 * Settles the marker from what `/auth/logout` did. Success or a 401 (no
 * session to end) clears it. Anything else — Go down, no network — keeps it,
 * so the next boot tries again.
 *
 * @returns Whether the session is known to be gone
 */
export async function confirmLogout(logout: () => Promise<void>): Promise<boolean> {
  try {
    await logout();
  } catch (err) {
    if (!isAxiosError(err) || err.response?.status !== 401) {
      return false;
    }
  }
  clearLogoutPending();
  return true;
}
